<template>
    <div>
      <!-- 骨架屏：仅在「加载中且当前无可渲染条目」时出现（见 showSkeleton），
           避免周切换命中缓存时用骨架替换已有内容造成闪烁 -->
      <ProductGridSkeleton v-if="showSkeleton" />

      <template v-else>
      <!-- 日期导航 -->
      <NavBar
      :dateRanges="dateRanges"
      :defaultSelectedDateRange="selectedDateRange"
      :showAllTimeTab="showAllTimeTab"
      :rangeCounts="rangeCounts"
      :allTimeCount="allTimeCount"
      :dayOptions="dayOptions"
      :dayCounts="dayCounts"
      :activeDay="activeDay"
      :dayPanelOpenable="hasDayData"
      @tab-selected="onDateRangeChange"
      @day-selected="onDaySelect"
    />
  
      <transition name="fade" @before-enter="beforeEnter" @enter="enter" @leave="leave">
        <div v-if="displayedItems.length" :key="selectedDateRange" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="(product, index) in displayedItems" :key="index" ref="productCard" class="product-card relative overflow-hidden grid grid-rows-[auto,1fr]">
            <!-- 分享按钮 -->
            <button 
              @click="openShareModal(index)"  
              class="absolute top-2 right-2 border px-3 py-1 rounded-sm transition-opacity duration-300 card-hover-btn"
              :title="$t('interface.share.card_button')"
            >
              <i class="fas fa-share-alt"></i>
            </button>
  
            <!-- logo 和 name/upvote 布局 -->
            <div class="flex items-center justify-start px-6 pt-5">
              <template v-if="isVideo(product.logo)">
                <a :href="productHref(product)" target="_blank" rel="noopener noreferrer nofollow" :title="product.name">
                  <video :src="product.logo" class="w-[80px] h-[80px] object-cover mr-4" autoplay muted loop playsinline :title="`${product.name} 视频预览`"></video>
                </a>
              </template>
              <template v-else>
                <a :href="productHref(product)" target="_blank" rel="noopener noreferrer nofollow" :title="$t('interface.product.logo_alt', { name: product.name })">
                  <img v-if="product.logo" :src="processLogoUrl(product.logo)" :alt="$t('interface.product.logo_alt', { name: product.name })" class="w-[80px] h-[80px] object-cover mr-4 max-w-none max-h-none" />
                </a>
              </template>

              <div>
                <a :href="product.website ? product.website : productHref(product)" target="_blank" rel="noopener noreferrer nofollow" :title="$t('interface.product.visit_website', { name: product.name })" class="product-name text-xl font-semibold transition-colors">
                  {{ product.name }}
                </a>
              <p class="text-sm text-gray-500 mt-3">
                UPVOTE 
                <span v-if="product.upvote.toLowerCase().includes('upvote')">
                  <span class="upvote-number">{{ product.upvote.split(' ')[1] }}</span>
                </span>
                <span v-else class="upvote-number">
                  {{ product.upvote }}
                </span>
              </p>
              <!-- 发布日期：取 featured_at 的日期部分（如 2026-08-12），仅日历图标 + 日期值，不留标签文案 -->
              <p v-if="featuredDate(product)" class="featured-date mt-2">
                <i class="fas fa-calendar-alt featured-date-icon mr-1"></i>
                <span class="featured-date-value">{{ featuredDate(product) }}</span>
              </p>
            </div>
            </div>
  
            <!-- 内容部分 -->
            <div class="px-6 py-4">
              <div class="text-gray-800 font-medium mb-2 chinese-description">
                <p>{{ product[`intro_${$i18n.locale}`] || product.中文简介 }}</p>
              </div>
              <div class="text-gray-600 text-sm leading-relaxed purpose-description">
                <p v-for="(part, index) in highlightedPurpose(product[`usage_${$i18n.locale}`] || product.用途说明)" :key="index" v-html="part"></p>
              </div>
              <div class="flex flex-wrap gap-2 mt-3">
                <router-link v-if="product.tag1" :to="`/${$i18n.locale}/tag/${product.tag1}`" class="tag" target="_blank" :title="$t('interface.product.view_more_tag', { tag: product.tag1 })">{{ product.tag1 }}</router-link>
                <router-link v-if="product.tag2" :to="`/${$i18n.locale}/tag/${product.tag2}`" class="tag" target="_blank" :title="$t('interface.product.view_more_tag', { tag: product.tag2 })">{{ product.tag2 }}</router-link>
                <router-link v-if="product.tag3" :to="`/${$i18n.locale}/tag/${product.tag3}`" class="tag" target="_blank" :title="$t('interface.product.view_more_tag', { tag: product.tag3 })">{{ product.tag3 }}</router-link>
              </div>
            </div>
            <!-- 图片展示区域 -->
            <div v-if="firstAvailableImages(product).length" class="w-full h-full max-h-[250px] overflow-hidden relative">
              <!-- 左箭头按钮，仅在图片数量大于1时显示 -->
              <button
                v-if="firstAvailableImages(product).length > 1"
                @click="previousImage(index)"
                class="arrow-btn arrow-btn-left"
                aria-label="Previous Image"
              >
                <i class="fas fa-chevron-left"></i>
              </button>

              <!-- 当前图片展示（点击打开大图预览灯箱） -->
              <div class="flex justify-center items-center image-wrapper">
                <img
                  :src="formatImageUrlsm(firstAvailableImages(product)[currentImageIndex[index]])"
                  :alt="$t('interface.product.preview_image', { name: product.name, index: currentImageIndex[index] + 1, total: firstAvailableImages(product).length })"
                  class="w-full h-auto object-cover card-image-clickable"
                  style="display: block; margin-left: auto; margin-right: auto; width: 100%; height: auto; object-fit: cover;"
                  @click="openLightbox(index)"
                />
              </div>

              <!-- 右箭头按钮，仅在图片数量大于1时显示 -->
              <button
                v-if="firstAvailableImages(product).length > 1"
                @click="nextImage(index)"
                class="arrow-btn arrow-btn-right"
                aria-label="Next Image"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>

          </div>
        </div>
      </transition>
      <!-- "显示更多"按钮 -->
      <button
        v-if="hasMoreItems"
        @click="loadMore"
        class="show-more-button mt-6 mx-auto block flex items-center space-x-2 px-6 py-1 transition-colors duration-200">
        <i class="fas fa-angle-double-down"></i>
        <span>{{ $t('interface.show_more') }}</span>
      </button>
      </template>
  
      <!-- 分享弹窗（独立组件，承载模板/样式/校验） -->
      <ShareModal :visible="showModal" :is-generating="isGenerating" @submit="onShareSubmit" @close="closeModal" />

      <!-- 大图预览灯箱（支持上一张/下一张切换） -->
      <ImageLightbox
        v-if="lightboxIndex !== null"
        :images="firstAvailableImages(this.displayedItems[lightboxIndex]).map(img => formatImageUrl(img))"
        :index="currentImageIndex[lightboxIndex]"
        :productName="displayedItems[lightboxIndex] ? displayedItems[lightboxIndex].name : ''"
        @navigate="onLightboxNavigate"
        @close="closeLightbox"
      />
    </div>
  </template>

<script>
import NavBar from './NavBar.vue'; // 导入导航条组件
import ImageLightbox from './ImageLightbox.vue'; // 大图预览灯箱（支持前后切换）
import ShareModal from './ShareModal.vue'; // 分享弹窗（公共组件）
import ProductGridSkeleton from './ProductGridSkeleton.vue'; // 数据加载期间的骨架屏
import { formatImageUrl, formatImageUrlsm } from '../utils/imgix'; // imgix 图片 URL 归一化（小图/大图）
import gridPaginationMixin from '../mixins/gridPaginationMixin'; // 分页状态机
import { parseFeaturedAt, collectDaysInProducts, countProductsByDay } from '../utils/dateHierarchy'; // 日筛选：featured_at 解析、分组与计数
import transitionMixin from '../mixins/transitionMixin'; // 淡入过渡钩子
import gridShareMixin from '../mixins/gridShareMixin'; // 分享逻辑（showModal/isGenerating/onShareSubmit 等）
import { resolveDataUrl } from '../utils/dataResolver'; // 数据文件外置后的 URL 解析（未迁移则原样返回）

export default {
    components: {
        ImageLightbox,
        NavBar,
        ProductGridSkeleton,
        ShareModal,
    },
    mixins: [gridPaginationMixin, transitionMixin, gridShareMixin],
    data() {
        return {
            highlightWordsList: [],
            itemsPerPage: 12, // 每页显示的条目数
            currentImageIndex: Array(this.products.length).fill(0), // 初始化所有图片索引为0
            lightboxIndex: null, // 当前打开大图预览的卡片索引，null 表示未打开
            // 分享图配置（gridShareMixin 默认值基础上覆盖）
      shareCardWidth: 420,
      shareGtagType: 'product',
      activeDay: null, // 当前日筛选（ymd 或 null），受控传给 NavBar；切周由 watch 清空
    };
    },
    watch: {
        // 仅 ProductGrid 需要：数据源变化时重置轮播图片索引
        products: {
        immediate: true,
        handler(newProducts) {
            if (newProducts.length) {
              this.currentImageIndex = Array(newProducts.length).fill(0); // 重置图片索引
            }
        }
        },
        // 切周清日筛选：选中周变化（含点周/年月跳转）时重置 activeDay
        selectedDateRange() {
          this.activeDay = null;
        },
        // 日过滤视图变化（含首屏 immediate）：重置分页与图片索引，使 displayedItems 始终跟随 displayedProducts
        // 用 nextTick 确保在 gridPaginationMixin.created 内注册的产品 watch（晚于本 watch）
        // 之后再次生效，避免整周数据覆盖日过滤子集
        displayedProducts: {
          immediate: true,
          handler(list) {
            this.$nextTick(() => {
              this.currentPage = 1;
              this.displayedItems = (list || []).slice(0, this.itemsPerPage);
              this.currentImageIndex = Array((list || []).length).fill(0);
            });
          },
        },
    },
    async mounted() {
        try {
            const response = await fetch(resolveDataUrl('data/highlightWords.json'));
            if (!response.ok) {
            throw new Error('Failed to load highlightWords.json');
            }
            const data = await response.json();
            this.highlightWordsList = data.highlightWords;
        } catch (error) {
            console.error('Error loading highlightWords.json:', error);
        }
    },
    props: {
      products: Array,
      dateRanges: Array,
      selectedDateRange: String,
      loading: { type: Boolean, default: false }, // 上游数据加载中（驱动骨架屏）
      showAllTimeTab: { type: Boolean, default: false }, // 透传给 NavBar：在周 tab 最右渲染「全部时间」tab
      rangeCounts: { type: Object, default: null }, // 透传给 NavBar：各周 tab 命中数
      allTimeCount: { type: Number, default: null } // 透传给 NavBar：全部时间 tab 命中数
    },
    computed: {
      // 是否渲染骨架屏：仅在「加载中」且「当前没有任何可渲染条目」时为真。
      // 判空是刻意的：切周时父级保留上一周 products 直到新数据到达，
      // 此时 loading 虽为 true 但不该用骨架覆盖已有卡片（否则每次切周都闪一下）。
      showSkeleton() {
        return this.loading && !this.products.length;
      },
      // 覆盖 gridPaginationMixin.hasMoreItems：基于日过滤后视图而非整周 products
      hasMoreItems() {
        return this.displayedItems.length < this.displayedProducts.length;
      },
      // 当前选中周内按 featured_at 分组去重升序的 ymd 数组（NavBar 「日期」按钮 chips 源）
      dayOptions() {
        return collectDaysInProducts(this.products);
      },
      // 每个 featured_at 日期在当前周内的条目数量，如 { "2026-08-17": 3, ... }
      // 供 NavBar 日 chip 后展示 (N)
      dayCounts() {
        return countProductsByDay(this.products);
      },
      // 当前周是否有 featured_at 数据（驱动 NavBar 「日期」按钮 disabled）
      hasDayData() {
        return this.dayOptions.length > 0;
      },
      // 实际渲染的条目：整周（activeDay=null）或仅当前日
      displayedProducts() {
        if (!this.activeDay) return this.products;
        return this.products.filter((p) => parseFeaturedAt(p && p.featured_at) === this.activeDay);
      },
    },
    methods: {
      previousImage(index) {
        if (this.currentImageIndex[index] > 0) {
          this.currentImageIndex[index]--;
        } else {
          this.currentImageIndex[index] = this.firstAvailableImages(this.displayedItems[index]).length - 1;
        }
      },
      nextImage(index) {
        if (this.currentImageIndex[index] < this.firstAvailableImages(this.displayedItems[index]).length - 1) {
          this.currentImageIndex[index]++;
        } else {
          this.currentImageIndex[index] = 0;
        }
      },
      // 打开大图预览灯箱（从卡片当前显示的图片进入）
      openLightbox(index) {
        this.lightboxIndex = index;
      },
      // 关闭大图预览灯箱
      closeLightbox() {
        this.lightboxIndex = null;
      },
      // 灯箱内前后切换：同步回卡片缩略图索引，保证关闭后卡片停在同图
      onLightboxNavigate(newIndex) {
        if (this.lightboxIndex === null) return;
        this.currentImageIndex[this.lightboxIndex] = newIndex;
      },
      // 获取产品跳转链接，兼容新旧数据结构
      // 上游数据源字段曾为 'styles_item__Dk_nz href'，新数据更新为 'group href'
      // 统一在此处解析，避免 key 散落多处维护
      productHref(product) {
        return product['group href'] || product['styles_item__Dk_nz href'] || '';
      },
      // 从 featured_at（ISO 时间串，如 "2026-08-12T07:01:00Z"）提取发布日期 YYYY-MM-DD
      featuredDate(product) {
        const raw = product && product.featured_at;
        if (!raw) return '';
        const m = String(raw).match(/^\d{4}-\d{2}-\d{2}/);
        return m ? m[0] : '';
      },
      // 获取有效的图片数组，兼容新旧数据结构
      firstAvailableImages(product) {
        // 接收当前渲染的条目（日过滤后为子集元素），不再按 index 访问 this.products
        // 如果 img 是数组，则直接返回，否则兼容旧结构
        return Array.isArray(product.img) && product.img.length > 0
          ? product.img
          : [product.img1, product.img2, product.img3].filter(Boolean);
      },
      // 图片 URL 处理（小图/大图）已抽到 ../utils/imgix，直接复用导入的函数
      formatImageUrl,
      formatImageUrlsm,
      // 分享文件名：品牌-名称-日期（Product 与 Funding/Channel 的差异仅此处，覆盖 mixin 默认）
      buildShareDownloadName(name) {
        return `${this.$t('interface.ai_inspiration_hub')}-${name}-${this.selectedDateRange.replace(/\s+/g, '')}.png`;
      },
      // 日筛选：由 NavBar 的 day-selected 事件回调（ymd 或 null=清除）
      onDaySelect(ymd) {
        this.activeDay = ymd;
      },
      // 覆盖 gridPaginationMixin.loadMore：分页累加基于日过滤后的 displayedProducts，保留 GA 上报
      loadMore() {
        if (this.isLoading || !this.hasMoreItems) return;
        this.isLoading = true;
        try {
          this.currentPage += 1;
          const source = this.displayedProducts;
          const start = this.displayedItems.length;
          const end = start + this.itemsPerPage;
          this.displayedItems = this.displayedItems.concat(source.slice(start, end));
          if (window.gtag) {
            window.gtag('event', `load_more_${this.paginationGtagLabel}`, {
              event_category: 'Pagination',
              event_label: this.paginationGtagLabel,
              value: this.displayedItems.length,
            });
          }
        } finally {
          this.isLoading = false;
        }
      },
      // 当日期范围发生变化时，通知父组件更新
      onDateRangeChange(selectedDateRange) {
        this.$emit('date-range-change', selectedDateRange); // 触发父组件事件，更新日期范围
      },
      // 高亮处理产品的用途说明（带关键词高亮，ProductGrid 独有）
      highlightedPurpose(purpose) {
        if (!purpose) return []; // 如果用途说明为空，返回空数组

        let highlightedDescription = purpose;
        // 遍历高亮词列表，使用正则表达式替换匹配的单词为高亮样式
        this.highlightWordsList.forEach((word) => {
          const regex = new RegExp(`(${word})`, 'g'); // 创建正则表达式匹配高亮词
          highlightedDescription = highlightedDescription.replace(regex, `<span class="highlight">${word}</span>`); // 替换为带有高亮样式的文本
        });

        // 返回分段后的高亮描述，每段通过 <p> 标签渲染
        return highlightedDescription.split('\n').map(part => part.trim()); // 按换行符拆分段落，并去除空格
      },
      // 判断给定的URL是否为视频链接
      isVideo(url) {
        return url && url.includes('mp4'); // 如果URL包含 'mp4' 则判定为视频
      },
      // 处理Logo的URL，移除指定的宽高参数
      processLogoUrl(url) {
        if (url) {
          if (url.includes('svg')) {
            return url.replace(/&w=72&h=72/g, '&w=150&h=150'); // 如果包含 "svg"，替换为 &w=150&h=150
          } else {
            return url.replace(/&w=\d+&h=\d+/g, ''); // 否则移除宽度和高度参数
          }
        }
        return url; // 如果没有URL，返回原始值
      },
    }
};
</script>

<style scoped>
/* 以下为 Product 专属样式；卡片容器/标签/简介/分享按钮/箭头/淡入等共用样式已提升到
   src/styles/cards.css（全局），Channel 的蓝色变体在其组件内 scoped 覆盖。 */

/* 产品名 —— 墨黑，hover 转黄铜 */
.product-name {
  color: var(--ink, #1b1815);
}

.product-name:hover {
  color: var(--brass, #b39a6b);
}

video {
  object-fit: cover;
}

/* 高亮文本样式 —— 黄铜强调（Product 关键词高亮专属） */
::v-deep .highlight {
  color: var(--brass, #b39a6b) !important;
  font-weight: 600;
}

/* 发布日期 —— 黄铜系日历图标 + 日期值，无「发布日期」标签文案 */
.featured-date {
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 12px;
  color: var(--text-muted, #8a8175);
}

.featured-date-icon {
  font-size: 12px;
  opacity: 0.85;
}

.featured-date-value {
  color: var(--brass, #b39a6b);
  font-weight: 600;
}
</style>
